import React, { ReactNode } from 'react';
import { Container, Center, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Image } from '@fiverr-private/media';
import { useBI, useImpression } from '../../bi';
import { stickyClassName, cardClassName, overlayClassName, nameClassName, quoteClassName } from './style.ve.css';

interface TestimonialCardProps {
    index: number;
    defaultImages: string[];
    hoverImages: string[];
    name: string;
    role?: ReactNode;
    quote: ReactNode;
    body: ReactNode;
}

/**
 * Presentational card — no GSAP. All animation (image cross-fade, text reveal)
 * is orchestrated by the parent section's useCardsAnimation hook.
 *
 * GSAP targets by data attribute:
 *   data-testimonial-card  — outer absolute container
 *   data-bg                — default image layer (scale)
 *   data-hover-layer       — hover image layer (opacity cross-fade)
 *   data-reveal            — text elements: name/role, desktop-quote, mobile-quote, body
 */
export const TestimonialCard = ({
    index,
    defaultImages,
    hoverImages,
    name,
    role,
    quote,
    body,
}: TestimonialCardProps) => {
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() => bi.reportTestimonialImpression({ testimonialId: name }));

    return (
        <Container
            ref={impressionRef}
            data-testimonial-card
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            style={{ zIndex: index }}
        >
            <Center height="100%" className={stickyClassName}>
                <Container position="relative" width="100%" overflow="hidden" className={cardClassName}>
                    {/* Default background layer — GSAP scales this container */}
                    <Container data-bg position="absolute" top={0} left={0} width="100%" height="100%">
                        {defaultImages.map((src, i) => (
                            <Image
                                key={i}
                                src={src}
                                alt=""
                                objectFit="cover"
                                position="absolute"
                                top={0}
                                left={0}
                                height="100%"
                            />
                        ))}
                    </Container>

                    {/* Hover layer — GSAP fades this in on scroll */}
                    <Container data-hover-layer position="absolute" top={0} left={0} width="100%" height="100%">
                        {hoverImages.map((src, i) => (
                            <Image
                                key={i}
                                src={src}
                                alt=""
                                objectFit="cover"
                                position="absolute"
                                top={0}
                                left={0}
                                height="100%"
                                loading="eager"
                            />
                        ))}
                        <Container
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            className={overlayClassName}
                        />
                    </Container>

                    {/* Text content */}
                    <Stack
                        direction="column"
                        justifyContent="spaceBetween"
                        gap="0"
                        position="absolute"
                        top={{ default: '17px', md: '52px' }}
                        left={{ default: '16px', md: '49px' }}
                        right={{ default: '16px', md: '49px' }}
                        bottom={{ default: '17px', md: '52px' }}
                        pointerEvents="none"
                    >
                        <Stack direction="row" justifyContent="spaceBetween" alignItems="flexStart" gap="0">
                            <Container data-reveal>
                                <Stack direction="column" gap="1">
                                    <Typography
                                        color="white"
                                        letterSpacing="-2px"
                                        fontWeight="semibold"
                                        className={nameClassName}
                                    >
                                        {name}
                                    </Typography>
                                    {role && (
                                        <Typography
                                            color="white"
                                            letterSpacing="-0.12px"
                                            fontWeight="normal"
                                            size={{ default: 'b_md', md: 'b_lg' }}
                                        >
                                            {role}
                                        </Typography>
                                    )}
                                </Stack>
                            </Container>

                            {/* Desktop quote — hidden on mobile */}
                            <Container data-reveal width="554px" hidden={{ default: true, md: false }}>
                                <Typography
                                    color="white"
                                    fontWeight="semibold"
                                    letterSpacing="-4px"
                                    textAlign="end"
                                    className={quoteClassName}
                                >
                                    {quote}
                                </Typography>
                            </Container>
                        </Stack>

                        <Stack direction="column" gap="6">
                            {/* Mobile quote — hidden on desktop */}
                            <Container data-reveal maxWidth={{ default: '100%' }} hidden={{ default: false, md: true }}>
                                <Typography
                                    color="white"
                                    fontWeight="semibold"
                                    letterSpacing="-0.28px"
                                    className={quoteClassName}
                                >
                                    {quote}
                                </Typography>
                            </Container>

                            <Container data-reveal width={{ default: '100%', md: '724px' }}>
                                <Typography
                                    color="white"
                                    fontWeight="light"
                                    letterSpacing="-1px"
                                    size={{ default: 'b_md', md: 'b_lg' }}
                                >
                                    {body}
                                </Typography>
                            </Container>
                        </Stack>
                    </Stack>
                </Container>
            </Center>
        </Container>
    );
};
